import { CASE_STATUS } from "@/constants";
import { useCaseErrors } from "@/hooks/cases/useCaseErrors";
import type { ChronologyRunActions } from "@/hooks/cases/useChronologyRun.types";
import { useCaseService } from "@/hooks/useCaseService";
import { invalidateCasesCache, updateCaseInCache } from "@/hooks/useCases";
import type { ChronologyRunResponse } from "@/services/caseServiceV2.types";
import { isConflictError } from "@/utils/errorUtils";
import { useCallback } from "react";

export const useChronologyRun = (): ChronologyRunActions => {
  const caseService = useCaseService();
  const { resolveAllCaseErrors } = useCaseErrors();

  const requestChronologyRun = useCallback(
    async (caseId: string, urgent = false): Promise<ChronologyRunResponse> => {
      try {
        const response = await caseService.requestChronologyRun(caseId, urgent);
        updateCaseInCache(caseId, { status_id: CASE_STATUS.AI_REVIEW });
        return response;
      } catch (error) {
        // A 409 doesn't say what the case's state is now, so refetch rather than patch.
        if (isConflictError(error)) invalidateCasesCache();
        throw error;
      }
    },
    [caseService]
  );

  const rerunChronology = useCallback(
    async (caseId: string): Promise<ChronologyRunResponse> => {
      await resolveAllCaseErrors(caseId);
      return requestChronologyRun(caseId);
    },
    [resolveAllCaseErrors, requestChronologyRun]
  );

  const fetchCaseStatus = useCallback(
    async (caseId: string): Promise<number> => (await caseService.getCaseDetails(caseId)).status_id,
    [caseService]
  );

  return { requestChronologyRun, rerunChronology, fetchCaseStatus };
};
